import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, Minus, Check } from "lucide-react";
import { CTA } from "../data/site";

export const Section = ({ children, className = "", id }) => (
  <section id={id} className={`py-20 md:py-28 ${className}`}>
    <div className="max-w-7xl mx-auto px-5 md:px-8">{children}</div>
  </section>
);

export const Heading = ({ overline, title, subtitle, center = true, as: Tag = "h2" }) => (
  <div className={`max-w-3xl mb-14 ${center ? "mx-auto text-center" : ""}`}>
    {overline && <span className="text-[#c9a24b] tracking-[0.3em] uppercase text-xs md:text-sm">{overline}</span>}
    <Tag className="font-serif-display text-4xl md:text-5xl text-[#f3ede0] mt-3 mb-5 leading-tight">{title}</Tag>
    <div className={`gold-divider w-40 mb-5 ${center ? "mx-auto" : ""}`} />
    {subtitle && <p className="text-[#a39d90] leading-relaxed text-lg">{subtitle}</p>}
  </div>
);

export const PageHero = ({ overline, title, subtitle, image }) => (
  <section className="relative pt-40 pb-20 md:pt-48 md:pb-28 overflow-hidden">
    <div className="absolute inset-0">
      {image && <img src={image} alt="" className="w-full h-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0b0b0b] via-[#0b0b0b]/85 to-[#0b0b0b]" />
    </div>
    <div className="relative max-w-4xl mx-auto px-5 md:px-8 text-center">
      {overline && <span className="text-[#e3c77a] tracking-[0.3em] uppercase text-xs md:text-sm">{overline}</span>}
      <h1 className="font-serif-display text-4xl md:text-6xl text-[#f3ede0] mt-4 mb-5 leading-tight">{title}</h1>
      {subtitle && <p className="text-[#cfc8b8] leading-relaxed text-lg max-w-2xl mx-auto">{subtitle}</p>}
    </div>
  </section>
);

export const GoldButton = ({ to, children, variant = "solid" }) => {
  const cls = variant === "solid"
    ? "bg-[#c9a24b] hover:bg-[#e3c77a] text-[#0b0b0b]"
    : "border border-[#f3ede0]/40 hover:border-[#c9a24b] text-[#f3ede0] hover:text-[#e3c77a]";
  return <Link to={to} className={`inline-flex items-center gap-2 font-medium tracking-wide px-8 py-4 transition-all duration-300 ${cls}`}>{children} <ArrowRight size={17} /></Link>;
};

export const CTASection = ({ title = "Ready to Build Your Custom Home?", text = "Tell us about your property and your vision. We'll help you take the first step toward a custom home built on your lot in Central Florida." }) => (
  <section className="py-20 md:py-28 bg-[#0f0f0f] border-y border-[#c9a24b]/15">
    <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
      <h2 className="font-serif-display text-3xl md:text-5xl text-[#f3ede0] mb-5">{title}</h2>
      <p className="text-[#a39d90] leading-relaxed mb-9 text-lg">{text}</p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <GoldButton to={CTA.primary.to}>{CTA.primary.label}</GoldButton>
        <GoldButton to={CTA.secondary.to} variant="outline">{CTA.secondary.label}</GoldButton>
      </div>
    </div>
  </section>
);

export const Bullets = ({ items }) => (
  <ul className="space-y-4">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3">
        <Check className="text-[#c9a24b] mt-1 shrink-0" size={18} />
        <span className="text-[#cfc8b8] leading-relaxed">{it}</span>
      </li>
    ))}
  </ul>
);

export const FAQ = ({ items }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className="max-w-3xl mx-auto divide-y divide-white/10 border-y border-white/10">
      {items.map((f, i) => (
        <div key={i}>
          <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 py-5 text-left">
            <span className="font-serif-display text-lg md:text-xl text-[#f3ede0]">{f.q}</span>
            {open === i ? <Minus className="text-[#c9a24b] shrink-0" size={20} /> : <Plus className="text-[#c9a24b] shrink-0" size={20} />}
          </button>
          <div className={`overflow-hidden transition-all duration-300 ${open === i ? "max-h-96 pb-5" : "max-h-0"}`}>
            <p className="text-[#a39d90] leading-relaxed">{f.a}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export const faqSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});
